import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/ui/shared/utils/cn'
import { ds } from './tokens'

export type SoftButtonVariant =
  | 'soft'
  | 'solid'
  | 'secondary'
  | 'danger'
  | 'ghost'
  | 'details'
  | 'detailsDanger'
  | 'icon'
  | 'iconDanger'

export type SoftButtonSize = 'md' | 'sm'

const variantClass: Record<SoftButtonVariant, string> = {
  soft: ds.btnSoft,
  solid: ds.btnSolid,
  secondary: ds.btnSecondary,
  danger: ds.btnDanger,
  ghost: ds.btnGhost,
  details: ds.btnDetails,
  detailsDanger: ds.btnDetailsDanger,
  icon: ds.btnIcon,
  iconDanger: ds.btnIconDanger,
}

const sizeClass: Record<SoftButtonSize, string> = {
  md: '',
  sm: 'h-9 max-h-9 px-3 text-xs',
}

export interface SoftButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: SoftButtonVariant
  size?: SoftButtonSize
  loading?: boolean
  icon?: ReactNode
  fullWidth?: boolean
  children?: ReactNode
}

const SoftButton = forwardRef<HTMLButtonElement, SoftButtonProps>(
  (
    {
      variant = 'soft',
      size = 'md',
      loading = false,
      icon,
      fullWidth = false,
      disabled,
      className,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isIconOnly = variant === 'icon' || variant === 'iconDanger'
    const isDanger =
      variant === 'danger' || variant === 'detailsDanger' || variant === 'iconDanger'

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(
          'ui-btn',
          isDanger && 'ui-btn-danger',
          variantClass[variant],
          sizeClass[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <svg
            className="h-4 w-4 motion-safe:animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : (
          icon
        )}
        {!isIconOnly && children}
        {isIconOnly && !icon && children}
      </button>
    )
  }
)

SoftButton.displayName = 'SoftButton'

export default SoftButton
